import React, { useState, useEffect } from "react";
import { 
  Plus, 
  Search, 
  Calendar, 
  Percent, 
  DollarSign, 
  Users,
  Trash2, 
  Edit2,
  Copy,
  X,
  Gift,
  Filter
} from "lucide-react";
import { toast } from "react-hot-toast";
import { adminCampaignService, DiscountCampaign } from "../services/adminCampaignService";

interface DiscountCampaignManagerProps {
  onClose?: () => void;
}

const DiscountCampaignManager: React.FC<DiscountCampaignManagerProps> = ({ onClose }) => {
  const [campaigns, setCampaigns] = useState<DiscountCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<DiscountCampaign | null>(null);
  const [processing, setProcessing] = useState(false);

  const [campaignForm, setCampaignForm] = useState({
    name: "",
    code: "",
    type: "percentage" as "percentage" | "fixed",
    value: "",
    description: "",
    usage_limit: "",
    expires_at: "",
    target_plans: [] as string[],
    minimum_plan_duration: ""
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await adminCampaignService.getCampaigns();
      if (response.success && response.campaigns) {
        setCampaigns(response.campaigns);
      }
    } catch (error) {
      console.error("Error loading campaigns:", error);
      toast.error("Failed to load discount campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    setProcessing(true);

    try {
      // Validate form
      if (!campaignForm.name || !campaignForm.type || !campaignForm.value) {
        toast.error("Please fill in all required fields");
        setProcessing(false);
        return;
      }

      const value = parseFloat(campaignForm.value);
      if (isNaN(value) || value <= 0) {
        toast.error("Please enter a valid discount value");
        setProcessing(false);
        return;
      }

      if (campaignForm.type === "percentage" && value > 100) {
        toast.error("Percentage discount cannot exceed 100%");
        setProcessing(false);
        return;
      }

      const data = {
        name: campaignForm.name,
        code: campaignForm.code || undefined,
        type: campaignForm.type,
        value: value,
        description: campaignForm.description || undefined,
        usage_limit: campaignForm.usage_limit ? parseInt(campaignForm.usage_limit) : null,
        expires_at: campaignForm.expires_at || undefined,
        target_plans: campaignForm.target_plans.length > 0 ? campaignForm.target_plans : ["pro", "premium"],
        minimum_plan_duration: campaignForm.minimum_plan_duration ? parseInt(campaignForm.minimum_plan_duration) : null
      };

      const response = await adminCampaignService.createCampaign(data);
      
      if (response.success && response.campaign) {
        toast.success("Discount campaign created successfully!");
        setCampaigns([response.campaign, ...campaigns]);
        setShowCreateModal(false);
        resetForm();
      } else {
        toast.error(response.error || "Failed to create campaign");
      }
    } catch (error) {
      console.error("Error creating campaign:", error);
      toast.error("Failed to create campaign");
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCampaign) return;

    setProcessing(true);

    try {
      const value = parseFloat(campaignForm.value);
      if (isNaN(value) || value <= 0) {
        toast.error("Please enter a valid discount value");
        setProcessing(false);
        return;
      }

      if (campaignForm.type === "percentage" && value > 100) {
        toast.error("Percentage discount cannot exceed 100%");
        setProcessing(false);
        return;
      }

      const data = {
        name: campaignForm.name,
        code: campaignForm.code,
        type: campaignForm.type,
        value: value,
        description: campaignForm.description || undefined,
        usage_limit: campaignForm.usage_limit ? parseInt(campaignForm.usage_limit) : null,
        expires_at: campaignForm.expires_at || undefined,
        target_plans: campaignForm.target_plans.length > 0 ? campaignForm.target_plans : ["pro", "premium"],
        minimum_plan_duration: campaignForm.minimum_plan_duration ? parseInt(campaignForm.minimum_plan_duration) : null
      };

      const response = await adminCampaignService.updateCampaign(selectedCampaign.id, data);
      
      if (response.success && response.campaign) {
        toast.success("Discount campaign updated successfully!");
        setCampaigns(campaigns.map(c => c.id === selectedCampaign.id ? response.campaign! : c));
        setShowEditModal(false);
        setSelectedCampaign(null);
        resetForm();
      } else {
        toast.error(response.error || "Failed to update campaign");
      }
    } catch (error) {
      console.error("Error updating campaign:", error);
      toast.error("Failed to update campaign");
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteCampaign = async (campaign: DiscountCampaign) => {
    if (!window.confirm(`Are you sure you want to delete the campaign "${campaign.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await adminCampaignService.deleteCampaign(campaign.id);
      
      if (response.success) {
        toast.success("Campaign deleted successfully!");
        setCampaigns(campaigns.filter(c => c.id !== campaign.id));
      } else {
        toast.error(response.error || "Failed to delete campaign");
      }
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast.error("Failed to delete campaign");
    }
  };

  const handleToggleStatus = async (campaign: DiscountCampaign) => {
    try {
      const newStatus = campaign.status === "active" ? "inactive" : "active";
      const response = await adminCampaignService.updateCampaignStatus(campaign.id, newStatus);
      
      if (response.success && response.campaign) {
        toast.success(`Campaign ${newStatus === "active" ? "activated" : "deactivated"} successfully!`);
        setCampaigns(campaigns.map(c => c.id === campaign.id ? response.campaign! : c));
      } else {
        toast.error(response.error || "Failed to update campaign status");
      }
    } catch (error) {
      console.error("Error updating campaign status:", error);
      toast.error("Failed to update campaign status");
    }
  };

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Discount code copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy code");
    }
  };

  const handleEditCampaign = (campaign: DiscountCampaign) => {
    setSelectedCampaign(campaign);
    setCampaignForm({
      name: campaign.name,
      code: campaign.code,
      type: campaign.type,
      value: campaign.value.toString(),
      description: campaign.description || "",
      usage_limit: campaign.usage_limit?.toString() || "",
      expires_at: campaign.expires_at ? new Date(campaign.expires_at).toISOString().slice(0, 16) : "",
      target_plans: campaign.target_plans || [],
      minimum_plan_duration: campaign.minimum_plan_duration?.toString() || ""
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setCampaignForm({
      name: "",
      code: "",
      type: "percentage",
      value: "",
      description: "",
      usage_limit: "",
      expires_at: "",
      target_plans: [],
      minimum_plan_duration: ""
    });
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = 
      campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || campaign.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 border-green-200";
      case "inactive":
        return "bg-gray-100 text-gray-800 border-gray-200";
      case "expired":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Gift className="h-6 w-6 mr-2 text-green-600" />
              Discount Campaigns
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Create and manage discount codes for marketing campaigns
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Campaign
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
      </div>

      {/* Campaigns List */}
      <div className="px-6 py-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <p className="mt-2 text-gray-600">Loading campaigns...</p>
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="text-center py-12">
            <Gift className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns found</h3>
            <p className="text-gray-600 mb-4">
              {campaigns.length === 0 
                ? "Get started by creating your first discount campaign"
                : "No campaigns match your current filters"
              }
            </p>
            {campaigns.length === 0 && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Campaign
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredCampaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(campaign.status)}`}>
                        {campaign.status}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4 mb-3">
                      <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                        <span className="font-mono font-semibold text-green-600">{campaign.code}</span>
                        <button
                          onClick={() => handleCopyCode(campaign.code)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                      
                      <div className="flex items-center gap-1 text-gray-700">
                        {campaign.type === "percentage" ? (
                          <Percent className="h-4 w-4 text-green-600" />
                        ) : (
                          <DollarSign className="h-4 w-4 text-green-600" />
                        )}
                        <span className="font-medium">
                          {campaign.type === "percentage" 
                            ? `${campaign.value}% OFF` 
                            : `$${campaign.value} OFF`
                          }
                        </span>
                      </div>

                      <div className="flex items-center gap-1 text-gray-600">
                        <Users className="h-4 w-4" />
                        <span className="text-sm">
                          {campaign.used_count} / {campaign.usage_limit || "∞"} used
                        </span>
                      </div>
                    </div>

                    {campaign.description && (
                      <p className="text-gray-600 mb-3">{campaign.description}</p>
                    )}

                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>
                          Starts: {new Date(campaign.starts_at).toLocaleDateString()}
                        </span>
                      </div>
                      {campaign.expires_at && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>
                            Expires: {new Date(campaign.expires_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleToggleStatus(campaign)}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                        campaign.status === "active"
                          ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                          : "bg-green-100 text-green-800 hover:bg-green-200"
                      }`}
                    >
                      {campaign.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                    
                    <button
                      onClick={() => handleEditCampaign(campaign)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    
                    <button
                      onClick={() => handleDeleteCampaign(campaign)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Create New Campaign</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleCreateCampaign} className="space-y-4">
              {/* Campaign Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Name *
                </label>
                <input
                  type="text"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({...campaignForm, name: e.target.value})}
                  placeholder="e.g., Summer Sale 2024"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              {/* Discount Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Code (Optional)
                </label>
                <input
                  type="text"
                  value={campaignForm.code}
                  onChange={(e) => setCampaignForm({...campaignForm, code: e.target.value.toUpperCase()})}
                  placeholder="e.g., SUMMER20 (auto-generated if empty)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to auto-generate a unique code
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Discount Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Type *
                  </label>
                  <select
                    value={campaignForm.type}
                    onChange={(e) => setCampaignForm({...campaignForm, type: e.target.value as "percentage" | "fixed"})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>

                {/* Discount Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Value *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={campaignForm.type === "percentage" ? "100" : undefined}
                    value={campaignForm.value}
                    onChange={(e) => setCampaignForm({...campaignForm, value: e.target.value})}
                    placeholder={campaignForm.type === "percentage" ? "e.g., 20" : "e.g., 5.00"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm({...campaignForm, description: e.target.value})}
                  placeholder="e.g., Special summer discount for new customers"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Usage Limit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Usage Limit
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={campaignForm.usage_limit}
                    onChange={(e) => setCampaignForm({...campaignForm, usage_limit: e.target.value})}
                    placeholder="e.g., 100 (unlimited if empty)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* Minimum Plan Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Plan Duration (days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={campaignForm.minimum_plan_duration}
                    onChange={(e) => setCampaignForm({...campaignForm, minimum_plan_duration: e.target.value})}
                    placeholder="e.g., 30 (no minimum if empty)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={campaignForm.expires_at}
                  onChange={(e) => setCampaignForm({...campaignForm, expires_at: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Campaign starts immediately upon creation
                </p>
              </div>

              {/* Target Plans */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Plans
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={campaignForm.target_plans.includes("pro")}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCampaignForm({
                            ...campaignForm,
                            target_plans: [...campaignForm.target_plans, "pro"]
                          });
                        } else {
                          setCampaignForm({
                            ...campaignForm,
                            target_plans: campaignForm.target_plans.filter(p => p !== "pro")
                          });
                        }
                      }}
                      className="mr-2"
                    />
                    Pro Plan
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={campaignForm.target_plans.includes("premium")}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCampaignForm({
                            ...campaignForm,
                            target_plans: [...campaignForm.target_plans, "premium"]
                          });
                        } else {
                          setCampaignForm({
                            ...campaignForm,
                            target_plans: campaignForm.target_plans.filter(p => p !== "premium")
                          });
                        }
                      }}
                      className="mr-2"
                    />
                    Premium Plan
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select which subscription plans this discount applies to
                </p>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {processing ? "Creating..." : "Create Campaign"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Campaign Modal */}
      {showEditModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Edit Campaign</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCampaign(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateCampaign} className="space-y-4">
              {/* Same form fields as create modal, but pre-populated */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Name *
                </label>
                <input
                  type="text"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({...campaignForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Code *
                </label>
                <input
                  type="text"
                  value={campaignForm.code}
                  onChange={(e) => setCampaignForm({...campaignForm, code: e.target.value.toUpperCase()})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Type *
                  </label>
                  <select
                    value={campaignForm.type}
                    onChange={(e) => setCampaignForm({...campaignForm, type: e.target.value as "percentage" | "fixed"})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Value *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={campaignForm.type === "percentage" ? "100" : undefined}
                    value={campaignForm.value}
                    onChange={(e) => setCampaignForm({...campaignForm, value: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm({...campaignForm, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Usage Limit
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={campaignForm.usage_limit}
                    onChange={(e) => setCampaignForm({...campaignForm, usage_limit: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Plan Duration (days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={campaignForm.minimum_plan_duration}
                    onChange={(e) => setCampaignForm({...campaignForm, minimum_plan_duration: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={campaignForm.expires_at}
                  onChange={(e) => setCampaignForm({...campaignForm, expires_at: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Start date is automatically set to current time (cannot be changed)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Plans
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={campaignForm.target_plans.includes("pro")}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCampaignForm({
                            ...campaignForm,
                            target_plans: [...campaignForm.target_plans, "pro"]
                          });
                        } else {
                          setCampaignForm({
                            ...campaignForm,
                            target_plans: campaignForm.target_plans.filter(p => p !== "pro")
                          });
                        }
                      }}
                      className="mr-2"
                    />
                    Pro Plan
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={campaignForm.target_plans.includes("premium")}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCampaignForm({
                            ...campaignForm,
                            target_plans: [...campaignForm.target_plans, "premium"]
                          });
                        } else {
                          setCampaignForm({
                            ...campaignForm,
                            target_plans: campaignForm.target_plans.filter(p => p !== "premium")
                          });
                        }
                      }}
                      className="mr-2"
                    />
                    Premium Plan
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedCampaign(null);
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {processing ? "Updating..." : "Update Campaign"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountCampaignManager;